import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, ViewChild, HostListener, ElementRef, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IconValidation, TableColumn } from '../table.interface';
import { DropdownOption } from 'src/ui/interfaces/dropdown.interfaces';
import { SearchableDropdownComponent, SearchableOption } from 'src/ui/components/dropdowns/searchable-dropdown/searchable-dropdown.component';
import { DropdownService } from 'src/utils/dropdown.service';
import { DropdownDateComponent } from 'src/ui/components/dropdowns/date/dropdown-date.component';

@Component({
  selector: 'app-table-body',
  standalone: true,
  imports: [CommonModule, FormsModule, SearchableDropdownComponent, DropdownDateComponent],
  styleUrls: ['./table-body.component.scss'],
  templateUrl: './table-body.component.html',
})
export class TableBodyComponent implements AfterViewInit, OnDestroy, OnChanges {
  constructor(
    private cdr: ChangeDetectorRef,
    private dropdownService: DropdownService
  ) { }

  // Tooltip de fila
  rowTooltipVisible = false;
  rowTooltipX = 0;
  rowTooltipY = 0;
  rowTooltipText = '';
  hoveredCell?: HTMLElement;
  private tooltipTimeout?: any;
  private isOverActions = false;
  private currentRow?: any;

  @Input() columns: TableColumn[] = [];
  @Input() paginatedData: any[] = [];
  @Input() allData: any[] = [];
  @Input() emptyRows: any[] = [];
  @Input() showSorting = true;
  @Input() icon1: string = '';
  @Input() icon2: string = '';
  @Input() icon3: string = '';
  @Input() icon1Tooltip: string = '';
  @Input() icon2Tooltip: string = '';
  @Input() icon3Tooltip: string = '';
  @Input() icon1Validation?: IconValidation;
  @Input() icon2Validation?: IconValidation;
  @Input() icon3Validation?: IconValidation;
  @Input() showIcon1Conditionally: boolean = false;
  @Input() showToggle = false;
  @Input() sortColumn: () => string | null = () => null;
  @Input() sortDirection: () => 'asc' | 'desc' = () => 'asc';
  @Input() resultsNotFoundText: string = '';
  @Input() isFirstColumnDark: boolean = false;
  @Input() enableRowClick: boolean = false;
  @Input() icon1ConditionalField: string = 'status';
  @Input() icon1ConditionalValues: string[] = ['borrador'];
  @Input() chipStyleClasses: { [key: string]: string } = {};
  @Input() filterOptions: { [key: string]: { label: string; value: string | number | boolean }[] } = {};
  @Input() filterOptionsLoading: boolean = false;

  @Output() iconAction1 = new EventEmitter<any>();
  @Output() iconAction2 = new EventEmitter<any>();
  @Output() iconAction3 = new EventEmitter<any>();
  @Output() toggleAction = new EventEmitter<{ row: any; checked: boolean }>();
  @Output() sortChange = new EventEmitter<{ column: string; direction: 'asc' | 'desc' }>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() dateFilterChange = new EventEmitter<{ column: string; startDate?: string; endDate?: string }>();
  @Output() multiSelectFilterChange = new EventEmitter<{ column: string; values: (string | number)[] }>();
  @Output() filterSearchChange = new EventEmitter<{ column: string; searchTerm: string }>();

  @ViewChild('tableContainer', { static: true }) tableContainer!: ElementRef<HTMLElement>;
  showColumnShadow = false;

  // Propiedades para filtros de fecha
  openDateFilter: string | null = null;
  dateFilters: { [key: string]: { start?: string; end?: string } } = {};
  dateFilterPosition: { top: number; left: number } = { top: 0, left: 0 };

  // Propiedades para filtros multi-select
  openMultiSelectFilter: string | null = null;
  multiSelectFilters: { [key: string]: (string | number)[] } = {};
  multiSelectFilterOptions: { [key: string]: SearchableOption[] } = {};
  multiSelectSelectedOptions: { [key: string]: SearchableOption[] } = {};

  // Propiedades para filtros de búsqueda de texto
  openTextSearchFilter: string | null = null;
  textSearchValues: { [key: string]: string } = {};

  ngAfterViewInit() {
    this.checkScroll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Cuando filterOptions cambian, reinicializar las opciones de los filtros multi-select
    if (changes['filterOptions'] && !changes['filterOptions'].firstChange) {
      const newFilterOptions = changes['filterOptions'].currentValue;

      // Reinicializar TODAS las columnas en filterOptions, incluso si están vacías
      Object.keys(newFilterOptions).forEach(columnKey => {
        const options = newFilterOptions[columnKey] || [];
        this.multiSelectFilterOptions[columnKey] = options.map((opt: any) => ({
          label: String(opt.label),
          value: typeof opt.value === 'boolean' ? String(opt.value) : opt.value
        }));
      });
      
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    // Limpieza si es necesaria
  }

  @HostListener('window:resize')
  onResize() {
    this.checkScroll();
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    // Window scroll handling if needed
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedInsideDropdown = target.closest('searchable-dropdown-dinamic-table');
    const clickedInsideDateFilter = target.closest('app-dropdown-date');
    
    // Verificar si el clic fue en un botón de filtro
    const clickedOnFilterButton = target.closest('button[title*="Filtrar"]');

    // Maneja el cierre del filtro multi-select
    if (this.openMultiSelectFilter) {
      if (!clickedInsideDropdown && !clickedOnFilterButton) {
        this.openMultiSelectFilter = null;
        this.dropdownService.closeDropdown();
      }
    }

    // Maneja el cierre del filtro de fecha
    if (this.openDateFilter) {
      if (!clickedInsideDateFilter && !clickedOnFilterButton) {
        this.openDateFilter = null;
        this.dropdownService.closeDropdown();
      }
    }

    // Maneja el cierre del filtro de texto
    if (this.openTextSearchFilter) {
      const clickedInsideTextSearch = target.closest('.text-search-panel');
      if (!clickedInsideTextSearch && !clickedOnFilterButton) {
        this.openTextSearchFilter = null;
      }
    }
  }

  onTableScroll() {
    this.checkScroll();
    // Actualizar posición del dropdown de fecha cuando hay scroll horizontal
    if (this.openDateFilter) {
      this.updateDateFilterScrollPosition();
    }
  }

  private checkScroll() {
    const el = this.tableContainer?.nativeElement;
    if (!el) {
      this.showColumnShadow = false;
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = el;
    this.showColumnShadow =
      scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth - 1;
  }

  validateIcon(row: any): boolean {
    if (!this.icon1Validation) return false;
    const invalidValues = this.icon1Validation.invalidValues || [];
    return invalidValues.includes(row[this.icon1Validation.columnKey]);
  }

  validateIcon2(row: any): boolean {
    if (!this.icon2Validation) return false;
    const invalidValues = this.icon2Validation.invalidValues || [];
    return invalidValues.includes(row[this.icon2Validation.columnKey]);
  }

  validateIcon3(row: any): boolean {
    if (!this.icon3Validation) return false;
    const invalidValues = this.icon3Validation.invalidValues || [];
    return invalidValues.includes(row[this.icon3Validation.columnKey]);
  }

  shouldShowIcon1ForRow(row: any): boolean {
    if (!this.icon1) {
      return false;
    }
    if (!this.showIcon1Conditionally) {
      return true;
    }

    const fieldValue = row[this.icon1ConditionalField];
    if (!fieldValue) {
      return false;
    }
    return this.icon1ConditionalValues.includes(fieldValue.toLowerCase());
  }

  onSortToggle(columnKey: string) {
    const column = this.columns.find(col => col.key === columnKey);
    if (column?.columnType === 'dateHourPicker') {
      this.toggleDateFilter(columnKey);
      return;
    }

    const currentDirection = this.sortColumn() === columnKey ? this.sortDirection() : 'desc';
    const newDirection: 'asc' | 'desc' = currentDirection === 'asc' ? 'desc' : 'asc';
    this.sortChange.emit({ column: columnKey, direction: newDirection });
  }

  onSort(columnKey: string, direction: 'asc' | 'desc') {
    this.sortChange.emit({ column: columnKey, direction });
  }

  toggleDateFilter(columnKey: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.openMultiSelectFilter) {
      this.openMultiSelectFilter = null;
      this.dropdownService.closeDropdown();
    }

    if (this.openTextSearchFilter) {
      this.openTextSearchFilter = null;
    }

    // Si ya está abierto, cerrarlo
    if (this.openDateFilter === columnKey) {
      this.openDateFilter = null;
      this.dropdownService.closeDropdown();
    } else {
      // Calcular posición relativa a la tabla (inicio de la primera columna)
      this.calculateDateFilterPosition(event);
      
      // Abrir el nuevo filtro
      this.openDateFilter = columnKey;
      setTimeout(() => {
        this.dropdownService.openDropdownDirect(`date-filter-${columnKey}`);
      }, 0);
    }
    
    this.cdr.detectChanges();
  }

  private calculateDateFilterPosition(event?: Event): void {
    // Con position: absolute relativo al wrapper (que tiene position: relative)
    // Las coordenadas son relativas al contenedor, no al viewport
    
    if (event) {
      const button = (event.target as HTMLElement).closest('button') as HTMLElement;
      const tableRect = this.tableContainer.nativeElement.getBoundingClientRect();
      
      if (button) {
        const buttonRect = button.getBoundingClientRect();
        // Calcular top relativo al contenedor de la tabla
        // buttonRect.bottom es relativo al viewport, tableRect.top también
        // La diferencia nos da la posición relativa al contenedor
        this.dateFilterPosition.top = buttonRect.bottom - tableRect.top + 5;
      }
    }
    
    // El left inicial es: padding de la primera columna (16px) - scroll horizontal
    const scrollLeft = this.tableContainer.nativeElement.scrollLeft;
    this.dateFilterPosition.left = 16 - scrollLeft;
  }

  private updateDateFilterScrollPosition(): void {
    // Actualizar solo el left cuando hay scroll horizontal
    const scrollLeft = this.tableContainer.nativeElement.scrollLeft;
    this.dateFilterPosition.left = 16 - scrollLeft;
  }

  onDateRangeSelected(columnKey: string, dateRange: { start: Date; end: Date }): void {
    const startString = this.toLocalISOString(dateRange.start);
    const endString = this.toLocalISOString(dateRange.end);

    this.dateFilters[columnKey] = {
      start: startString,
      end: endString
    };

    this.dateFilterChange.emit({
      column: columnKey,
      startDate: startString,
      endDate: endString
    });

    this.openDateFilter = null;
  }

  onDateRangeCleared(columnKey: string): void {
    delete this.dateFilters[columnKey];
    this.dateFilterChange.emit({ column: columnKey, startDate: undefined, endDate: undefined });
    this.openDateFilter = null;
  }

  private toLocalISOString(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  // ============ MÉTODOS PARA FILTROS DE BÚSQUEDA DE TEXTO ============

  toggleTextSearchFilter(columnKey: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (this.openTextSearchFilter === columnKey) {
      this.openTextSearchFilter = null;
    } else {
      if (this.openMultiSelectFilter) {
        this.openMultiSelectFilter = null;
        this.dropdownService.closeDropdown();
      }
      if (this.openDateFilter) {
        this.openDateFilter = null;
        this.dropdownService.closeDropdown();
      }
      this.openTextSearchFilter = columnKey;
    }
    this.cdr.detectChanges();
  }

  hasActiveTextSearch(columnKey: string): boolean {
    return !!(this.textSearchValues[columnKey]?.trim());
  }

  hasActiveDateFilter(columnKey: string): boolean {
    const f = this.dateFilters[columnKey];
    return !!(f && (f.start || f.end));
  }

  onTextSearchInput(columnKey: string, value: string): void {
    this.textSearchValues[columnKey] = value;
    this.filterSearchChange.emit({ column: columnKey, searchTerm: value });
  }

  clearTextSearch(columnKey: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.textSearchValues[columnKey] = '';
    this.filterSearchChange.emit({ column: columnKey, searchTerm: '' });
    this.openTextSearchFilter = null;
    this.cdr.detectChanges();
  }

  // ============ MÉTODOS PARA FILTROS MULTI-SELECT ============

  toggleMultiSelectFilter(columnKey: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.stopImmediatePropagation();
      event.preventDefault();
    }

    // Caso: clic en el mismo dropdown abierto → cerrar
    if (this.openMultiSelectFilter === columnKey) {
      this.openMultiSelectFilter = null;
      this.dropdownService.closeDropdown();
      this.cdr.detectChanges();
      return;
    }

    // Cierra el dropdown anterior si existe
    if (this.openMultiSelectFilter) {
      this.dropdownService.closeDropdown();
    }

    // Cierra el filtro de fecha si está abierto
    if (this.openDateFilter) {
      this.openDateFilter = null;
    }

    // Cierra el filtro de texto si está abierto
    if (this.openTextSearchFilter) {
      this.openTextSearchFilter = null;
    }

    // Inicializa las opciones si no existen (aunque estén cargando, el dropdown mostrará su loading interno)
    if (!this.multiSelectFilterOptions[columnKey]) {
      this.initializeFilterOptions(columnKey);
    }

    // Abre el dropdown (mostrará loading interno si filterOptionsLoading = true)
    this.openMultiSelectFilter = columnKey;
    this.cdr.detectChanges();
    
    // Abre el dropdown después de renderizar
    setTimeout(() => {
      this.dropdownService.openDropdownDirect(`multi-select-${columnKey}`);
    }, 0);
  }

  /**
   * Verifica si una columna tiene filtros activos seleccionados
   */
  hasActiveFilters(columnKey: string): boolean {
    return this.multiSelectSelectedOptions[columnKey]?.length > 0;
  }

  onMultiSelectChange(columnKey: string, selectedOptions: SearchableOption[]): void {
    this.multiSelectFilters[columnKey] = selectedOptions.map(opt => opt.value);
    this.multiSelectSelectedOptions[columnKey] = selectedOptions;
    
    // Siempre emitir el cambio, incluso si está vacío
    this.multiSelectFilterChange.emit({ 
      column: columnKey, 
      values: this.multiSelectFilters[columnKey] 
    });
  }

  /**
   * Maneja el evento de búsqueda en el filtro multi-select
   */
  onFilterSearchInput(columnKey: string, searchTerm: string | null): void {
    // Emitir el evento de búsqueda al componente padre
    this.filterSearchChange.emit({ 
      column: columnKey, 
      searchTerm: searchTerm || '' 
    });
  }

  applyMultiSelectFilter(columnKey: string): void {
    const values = this.multiSelectFilters[columnKey] || [];
    this.multiSelectFilterChange.emit({ column: columnKey, values });
    this.openMultiSelectFilter = null;
  }

  clearMultiSelectFilter(columnKey: string): void {
    if (this.multiSelectFilters[columnKey]) {
      delete this.multiSelectFilters[columnKey];
    }
    if (this.multiSelectSelectedOptions[columnKey]) {
      delete this.multiSelectSelectedOptions[columnKey];
    }
    this.multiSelectFilterChange.emit({ column: columnKey, values: [] });
  }

  getAllActiveFilters(): { [key: string]: (string | number)[] } {
    const activeFilters: { [key: string]: (string | number)[] } = {};
    
    Object.keys(this.multiSelectFilters).forEach(key => {
      if (this.multiSelectFilters[key] && this.multiSelectFilters[key].length > 0) {
        activeFilters[key] = this.multiSelectFilters[key];
      }
    });

    return activeFilters;
  }

  initializeFilterOptions(columnKey: string): void {
    // Si filterOptions tiene esta columna definida (incluso si está vacía), usar esas opciones
    // Esto previene extraer de los datos cuando el endpoint falla o retorna vacío
    if (this.filterOptions && columnKey in this.filterOptions) {
      this.multiSelectFilterOptions[columnKey] = (this.filterOptions[columnKey] || []).map(opt => ({
        label: String(opt.label),
        value: typeof opt.value === 'boolean' ? String(opt.value) : opt.value
      }));
      
      if (this.multiSelectFilterOptions[columnKey].length > 0) {
        console.log(`✅ Usando ${this.multiSelectFilterOptions[columnKey].length} opciones predefinidas para "${columnKey}"`);
      } else {
        console.log(`ℹ️ No hay opciones disponibles para "${columnKey}" (endpoint retornó vacío o falló)`);
      }
      return;
    }

    // Solo como fallback: extraer de los datos si la columna NO está definida en filterOptions
    const dataSource = this.allData.length > 0 ? this.allData : this.paginatedData;
    const uniqueValues = new Set<string | number>();

    dataSource.forEach(row => {
      const value = row[columnKey];
      if (value !== null && value !== undefined && value !== '') {
        uniqueValues.add(value);
      }
    });

    this.multiSelectFilterOptions[columnKey] = Array.from(uniqueValues)
      .sort()
      .map(value => ({ label: String(value), value }));
    console.log(`⚠️ Extrayendo ${this.multiSelectFilterOptions[columnKey].length} opciones de datos para "${columnKey}" (fallback)`);
  }

  toggleRow(row: any) {
    const proposedState = !row.toggleActive;
    this.toggleAction.emit({ row, checked: proposedState });
  }

  getStatusClasses(status: string): object {
    if (!status) {
      return {};
    }

    const lowerCaseStatus = status.toLowerCase();

    const greenStatus = ['activo', 'disponible'];
    const redStatus = ['inactivo', 'vencido'];
    const orangeStatus = ['suspendido'];
    const grayStatus = ['eliminado'];
    const yellowStatus = ['pendiente', 'pago pendiente'];
    const secondaryStatus = ['borrador', 'test'];

    if (greenStatus.includes(lowerCaseStatus)) {
      return { 'bg-chip-green': true, 'text-text-primary': true };
    }

    if (redStatus.includes(lowerCaseStatus)) {
      return { 'bg-chip-red': true, 'text-text-primary': true };
    }

    if (orangeStatus.includes(lowerCaseStatus)) {
      return { 'bg-chip-orange': true, 'text-text-primary': true };
    }

    if (grayStatus.includes(lowerCaseStatus)) {
      return { 'bg-gray-200': true, 'text-text-primary': true };
    }

    if (yellowStatus.includes(lowerCaseStatus)) {
      return { 'bg-chip-yellow': true, 'text-text-primary': true };
    }

    if (secondaryStatus.includes(lowerCaseStatus)) {
      return { 'bg-secundary-b1200': true, 'text-text-primary': true };
    }

    return { 'bg-gray-200': true, 'text-text-primary': true };
  }

  onRowClick(row: any, event: Event) {
    if (this.enableRowClick) {
      const target = event.target as HTMLElement;
      if (target.tagName === 'BUTTON' || target.tagName === 'INPUT' || target.closest('button')) {
        return;
      }
      this.rowClick.emit(row);
    }
  }

  getRowCursorStyle(): string {
    return this.enableRowClick ? 'cursor-pointer' : '';
  }

  onRowMouseEnter(): void {
    if (this.enableRowClick) {
      this.rowTooltipText = 'Ver detalles';
    }
  }

  onRowMouseLeave(): void {
    this.clearTooltipTimeout();
    this.rowTooltipVisible = false;
    this.hoveredCell = undefined;
    this.rowTooltipText = '';
    this.isOverActions = false;
  }

  onRowMouseMove(event: MouseEvent, row?: any): void {
    this.currentRow = row;
    this.clearTooltipTimeout();
    this.rowTooltipVisible = false;
    this.rowTooltipX = event.clientX;
    this.rowTooltipY = event.clientY - 40;

    const target = event.target as HTMLElement;
    const cell = target.closest('td');

    if (cell) {
      this.hoveredCell = cell;

      this.tooltipTimeout = setTimeout(() => {
        if (this.isOverActions) {
          this.updateTooltipTextForIcon(target);
        } else {
          this.updateTooltipText(cell);
        }
        if (this.rowTooltipText) {
          this.rowTooltipVisible = true;
          this.cdr.detectChanges();
        }
      }, 300);
    }
  }

  private updateTooltipTextForIcon(target: HTMLElement): void {
    const button = target.closest('button');

    if (button) {
      if (button.hasAttribute('aria-pressed')) {
        if (this.currentRow) {
          this.rowTooltipText = this.currentRow.toggleActive ? 'Desactivar' : 'Activar';
        } else {
          this.rowTooltipText = '';
        }
        return;
      }

      const actionsDiv = button.closest('div');
      if (actionsDiv) {
        const allButtons = Array.from(actionsDiv.querySelectorAll('button:not([aria-pressed])'));
        const buttonIndex = allButtons.indexOf(button as HTMLButtonElement);

        let currentIndex = 0;
        if (this.icon1) {
          if (currentIndex === buttonIndex) {
            this.rowTooltipText = this.icon1Tooltip;
            return;
          }
          currentIndex++;
        }
        if (this.icon3) {
          if (currentIndex === buttonIndex) {
            this.rowTooltipText = this.icon3Tooltip;
            return;
          }
          currentIndex++;
        }
        if (this.icon2) {
          if (currentIndex === buttonIndex) {
            this.rowTooltipText = this.icon2Tooltip;
            return;
          }
          currentIndex++;
        }
      }
    }

    this.rowTooltipText = '';
  }

  private clearTooltipTimeout() {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = undefined;
    }
  }

  private updateTooltipText(cell: HTMLElement): void {
    let isTruncated = false;
    let fullText = '';

    if (cell.scrollWidth > cell.clientWidth || cell.scrollHeight > cell.clientHeight) {
      isTruncated = true;
      fullText = cell.textContent?.trim() || '';
    }

    if (!isTruncated) {
      const textElements = cell.querySelectorAll('span, div, td');

      for (let i = 0; i < textElements.length; i++) {
        const element = textElements[i] as HTMLElement;
        const computedStyle = window.getComputedStyle(element);

        if (element.scrollWidth > element.clientWidth ||
          element.scrollHeight > element.clientHeight ||
          (computedStyle.textOverflow === 'ellipsis' && element.scrollWidth > element.clientWidth)) {
          isTruncated = true;
          fullText = element.textContent?.trim() || '';
          break;
        }
      }
    }

    if (isTruncated && fullText) {
      this.rowTooltipText = fullText;
    } else if (this.enableRowClick) {
      this.rowTooltipText = 'Ver detalles';
    } else {
      this.rowTooltipText = '';
    }
  }

  onActionsMouseEnter(): void {
    this.isOverActions = true;
  }

  onActionsMouseLeave(): void {
    this.isOverActions = false;
    if (this.enableRowClick) {
      this.rowTooltipText = 'Ver detalles';
    }
  }
}
